
import { useEffect, useMemo, useState } from "react";
import { useAuth } from '@/contexts/AuthContext';
import type { Client } from "@/lib/types";
import { useClient } from "@/context/client-context";
import { getClients } from '@/components/clients/services/clients-api';
import logger from '@/lib/logging';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { clientDisplayLabel, clientSearchHaystack } from '@/lib/clients/roster-fields';

export function SelectClientDialog({
  children,
  open,
  onOpenChange,
}: {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const { setClient } = useClient();
  const { loading: authLoading, isAuthenticated } = useAuth();

  // Client list MUST go through `/api/protected/clients` (same as ClientsCache) — avoids
  // parallel Firestore onSnapshot subscribers that violate rules (and fail smoke consoles).
  useEffect(() => {
    if (!open) {
      setLoading(false);
      return;
    }
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!isAuthenticated) {
      setClients([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getClients()
      .then((clientsData) => {
        if (cancelled) return;
        setClients(clientsData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('[SelectClientDialog] Error fetching clients:', err);
        setClients([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, authLoading, isAuthenticated]);

  // Shared with the roster's search: matches name, email, AND customId.
  // Haystacks are built once per client list (not per keystroke) and reused
  // across searches.
  const clientHaystacks = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clients) {
      map.set(
        client.id,
        clientSearchHaystack(client as unknown as Record<string, unknown>).join(' ')
      );
    }
    return map;
  }, [clients]);

  const filteredClients = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return clients;
    const tokens = normalized.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return clients;
    return clients.filter((client) => {
      const haystack = clientHaystacks.get(client.id) ?? '';
      return tokens.every((token) => haystack.includes(token));
    });
  }, [clients, searchTerm, clientHaystacks]);

  const handleSelectClient = (client: Client) => {
    setClient(client);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" aria-describedby="select-client-description">
        <DialogHeader>
          <DialogTitle>Select a Client</DialogTitle>
          <DialogDescription id="select-client-description">
            Choose a client to view their assessment data and progress. This will load their context across the application.
          </DialogDescription>
        </DialogHeader>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
          <div className="flex items-center border-b px-3">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {!isAuthenticated ? (
              <div className="py-6 text-center text-sm">Sign in to load clients.</div>
            ) : loading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="py-6 text-center text-sm">No clients found.</div>
            ) : (
              <div className="overflow-hidden p-1 text-foreground">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectClient(client);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select client ${clientDisplayLabel(client)}`}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-3 py-3 text-base outline-none hover:bg-accent hover:text-accent-foreground transition-colors focus:bg-accent focus:text-accent-foreground touch-target-min"
                  >
                    {clientDisplayLabel(client)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
